import { useEffect, useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAppContext } from "components/Context/AppContext";

const DASHBOARD_API =
  process.env.REACT_APP_API_URL || "http://localhost:5050/api";

const NAV_TABS = [
  { label: "The Big Picture",  path: "/summary" },
  { label: "Holdings",         path: "/holdings" },
  { label: "Risk",             path: "/risk" },
  { label: "Alternatives",     path: "/alternatives" },
  { label: "Stress Scenarios", path: "/stress" },
  { label: "What-if",          path: "/whatif" },
  { label: "Historical",       path: "/historical" },
  { label: "Security Level",   path: "/security-level" },
  { label: "Portfolios",       path: "/portfolios" },
  { label: "Settings",         path: "/settings" },
];

const THEME_OPTIONS = [
  { key: "dark",  label: "Dark" },
  { key: "light", label: "Light" },
];

const displayName = (account) =>
  account?.short_name || account?.account_name || "—";

export default function DashboardMenuBar({ onThemeChange }) {
  const { selectedAccount, setSelectedAccount } = useAppContext();
  const location = useLocation();
  const [accounts, setAccounts]   = useState([]);
  const [accOpen, setAccOpen]     = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);
  const [theme, setTheme]         = useState(() => localStorage.getItem("trg_theme") || "dark");
  const accRef   = useRef(null);
  const themeRef = useRef(null);
  const accMenuRef   = useRef(null);
  const themeMenuRef = useRef(null);

  // Fetch accounts
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) return;
    fetch(`${DASHBOARD_API}/accounts?token=${token}`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data) || !data.length) return;
        setAccounts(data);
        // If stored account isn't in the fetched list (stale / wrong user), reset it
        const stored = sessionStorage.getItem("selectedAccount");
        const storedId = stored ? JSON.parse(stored)?.account_id : null;
        const valid = storedId && data.some((a) => a.account_id === storedId);
        if (!valid) {
          const def = data.find((a) => a.is_default) ?? data[0];
          setSelectedAccount(def);
        }
      })
      .catch(console.error);
  }, []);

  // Apply theme globally so it affects every tab (persists via localStorage)
  useEffect(() => {
    document.body.classList.toggle("theme-light", theme === "light");
  }, [theme]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (accRef.current && !accRef.current.contains(e.target)) setAccOpen(false);
      if (themeRef.current && !themeRef.current.contains(e.target)) setThemeOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Position dropdown menus below their triggers
  const positionMenu = (triggerRef, menuRef) => {
    if (!triggerRef.current || !menuRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    menuRef.current.style.top  = `${rect.bottom + 1}px`;
    menuRef.current.style.left = `${rect.left}px`;
  };

  const handleAccOpen = () => {
    const opening = !accOpen;
    setAccOpen(opening);
    if (opening) setTimeout(() => positionMenu(accRef, accMenuRef), 0);
  };

  const handleThemeOpen = () => {
    const opening = !themeOpen;
    setThemeOpen(opening);
    if (opening) setTimeout(() => positionMenu(themeRef, themeMenuRef), 0);
  };

  const handleSelectAccount = (acc) => {
    setSelectedAccount(acc);
    setAccOpen(false);
  };

  const handleSetTheme = (t) => {
    setTheme(t);
    setThemeOpen(false);
    try { localStorage.setItem("trg_theme", t); } catch (e) {}
    if (onThemeChange) onThemeChange(t);
  };

  const canSwitch = accounts.length >= 1;

  return (
    <nav className="trg-tabbar">

      {/* Accounts dropdown */}
      <div className="trg-dropdown" ref={accRef}>
        <div
          className={`trg-dropdown-trigger${accOpen ? " open" : ""}`}
          onClick={canSwitch ? handleAccOpen : undefined}
          style={{ cursor: canSwitch ? "pointer" : "default" }}
        >
          {selectedAccount ? displayName(selectedAccount) : "Accounts"}
          {canSwitch && (
            <svg className="chevron" width="10" height="10" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          )}
        </div>
        {accOpen && (
          <div className="trg-dropdown-menu" ref={accMenuRef}>
            <div className="trg-dropdown-label">Switch Account</div>
            {accounts.map((acc) => {
              const isSelected = acc.account_id === selectedAccount?.account_id;
              return (
                <div
                  key={acc.account_id}
                  className={`trg-dropdown-item${isSelected ? " selected" : ""}`}
                  onClick={() => handleSelectAccount(acc)}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="8" r="4" />
                    <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                  </svg>
                  {displayName(acc)}
                  {isSelected && (
                    <span style={{ marginLeft: "auto", color: "var(--dash-gold-text)" }}>✓</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Page tabs (scrollable middle; account left + theme right stay pinned) */}
      <div className="trg-tabs-scroll">
        {NAV_TABS.map((tab) => {
          if (!tab.path) {
            return (
              <div key={tab.label} className="trg-tab disabled">
                {tab.label}
              </div>
            );
          }
          const isActive = location.pathname === tab.path;
          return (
            <Link
              key={tab.label}
              to={tab.path}
              className={`trg-tab${isActive ? " active" : ""}`}
            >
              {tab.label}
            </Link>
          );
        })}
      </div>

      {/* Theme dropdown — pushed to right */}
      <div className="trg-dropdown" ref={themeRef} style={{ marginLeft: "auto" }}>
        <div
          className={`trg-dropdown-trigger${themeOpen ? " open" : ""}`}
          onClick={handleThemeOpen}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
          Theme
          <svg className="chevron" width="10" height="10" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        {themeOpen && (
          <div
            className="trg-dropdown-menu"
            ref={themeMenuRef}
            style={{ minWidth: 160, right: 0, left: "auto" }}
          >
            {THEME_OPTIONS.map((opt) => (
              <div
                key={opt.key}
                className={`trg-dropdown-item${theme === opt.key ? " selected" : ""}`}
                onClick={() => handleSetTheme(opt.key)}
              >
                {opt.key === "dark" ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                ) : (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                )}
                {opt.label}
                {theme === opt.key && (
                  <span style={{ marginLeft: "auto", color: "var(--dash-gold-text)" }}>✓</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

    </nav>
  );
}
